import type React from 'react';

import * as Yup from 'yup';
import { ChecklistDrawerTitle } from './ChecklistDrawerTitle';
import { DrawerBody } from './DrawerBody/DrawerBody';
import { DRAWER_WIDTH } from './styles';
import { Formik } from 'formik';

import {
  type Checklist,
  CHECKLIST_VERSION,
  type ChecklistField,
  type TemplateChecklist,
  type TemplateChecklistField,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { Drawer } from '@cobliteam/base-components';

const getTemplateChecklistFields = (
  fields: ChecklistField[]
): TemplateChecklistField[] =>
  fields.map(({ id, ...templateChecklistField }) => ({
    ...templateChecklistField,
  }));

export interface ChecklistDrawerProps {
  open: boolean;
  hasLoadingRequests: boolean;
  onCloseDrawer: () => void;
  handleOnSubmit: (checklist: TemplateChecklist) => void;
  onDeleteClickButton: (checklist: Checklist) => void;
  checklistNames: Map<string, string>;
  name: string;
  checklist?: Checklist;
}

export const ChecklistDrawer: React.FC<ChecklistDrawerProps> = ({
  open,
  hasLoadingRequests,
  onCloseDrawer,
  handleOnSubmit,
  onDeleteClickButton,
  name,
  checklistNames,
  checklist,
}) => {
  const TitleComponent = <ChecklistDrawerTitle />;

  const handleCloseDrawer = () => {
    Analytics.track('Checklist Drawer Closed');

    onCloseDrawer();
  };

  return (
    <Formik
      initialValues={{
        checklistName: name,
        checklistItems: checklist
          ? getTemplateChecklistFields(checklist.fields)
          : [],
      }}
      enableReinitialize
      validationSchema={Yup.object().shape({
        checklistName: Yup.string()
          .required('Campo obrigatório')
          .test(
            'is-checklist-name-duplicated',
            'Já existe uma checklist com esse nome',
            (value) => {
              const hasDuplicatedChecklistNameOnList =
                value && checklistNames.has(value.toLowerCase());
              const hasDuplicatedNameAndIsANewChecklist =
                hasDuplicatedChecklistNameOnList && !checklist;
              const hasDuplicatedNameAndIsAnAlreadyExistentChecklist =
                checklist && hasDuplicatedChecklistNameOnList;
              const hasDuplicatedNameAndIsDifferentChecklists =
                checklist &&
                checklist.id !== checklistNames.get(value?.toLowerCase() || '');

              return !(
                hasDuplicatedNameAndIsANewChecklist ||
                (hasDuplicatedNameAndIsAnAlreadyExistentChecklist &&
                  hasDuplicatedNameAndIsDifferentChecklists)
              );
            }
          ),
        checklistItems: Yup.array()
          .of(
            Yup.object().shape({
              type: Yup.string().required(),
              required: Yup.boolean().required(),
              title: Yup.string().required('Campo obrigatório'),
              content: Yup.object().shape({
                checked: Yup.boolean().nullable(),
                value: Yup.string().nullable(),
                options: Yup.array()
                  .of(
                    Yup.object().shape({
                      label: Yup.string().required('Campo obrigatório'),
                      checked: Yup.boolean().nullable(),
                    })
                  )
                  .min(1),
              }),
            })
          )
          .min(1),
      })}
      validateOnChange={false}
      onSubmit={(values, actions) => {
        const { checklistName, checklistItems } = values;
        const getNewChecklist = () => {
          const templateChecklist = {
            version: CHECKLIST_VERSION,
            name: checklistName,
            fields: checklistItems,
          };

          if (!checklist) {
            return {
              ...templateChecklist,
              template: true,
            };
          }

          return templateChecklist;
        };

        handleOnSubmit(getNewChecklist());
        // TODO: Check if api request was successful, then set this to false
        actions.setSubmitting(false);
      }}
    >
      <Drawer
        closeOnClickOutside
        open={open}
        title={TitleComponent}
        body={
          <DrawerBody
            onCancelClickButton={handleCloseDrawer}
            onDeleteClickButton={onDeleteClickButton}
            hasLoadingRequests={hasLoadingRequests}
            checklist={checklist}
          />
        }
        onClose={handleCloseDrawer}
        titleDivider={false}
        customWidth={DRAWER_WIDTH}
        stickyHeader
      />
    </Formik>
  );
};

export default ChecklistDrawer;
